<script setup lang="ts">
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, ZhSystemLabel } from '@zerohuman/ui'
import { computed, ref } from 'vue'
import { API_TOKEN_RESOURCES } from '@/api'
import {
    applyPresetLevels,
    everyResourceAt,
    GRANT_PRESETS,
    type GrantPreset,
    type GrantPresetKey,
    isCustomizedFromPreset,
} from '@/composables/useGrantPresets'
import type { ScopeLevel } from '@/composables/useScopeLevels'
import { resourceLabel } from '@/lib/resourceLabels'

/**
 * The grant editor's preset picker (#628): named starting points an owner can apply in one action and then
 * adjust, with the per-resource picker underneath and an indicator once the grant has drifted from whichever
 * preset produced it.
 *
 * The picker owns no grant of its own. It reads `levels` and emits the next `levels`, so whoever is granting
 * — an invite form, an edit-grant panel — keeps the one copy of the answer and sends the same
 * `{ isOwner?, scopes? }` patch that exists today. Nothing records which preset produced a grant: a preset is
 * a starting point at the moment of granting, not a role that is saved.
 */
const props = withDefaults(
    defineProps<{
        /** The picker's own per-resource levels (the `useScopeLevels` shape), changed in place when applied. */
        levels: Record<string, ScopeLevel>
        /** What the inviter themself holds: a preset never proposes more than this. */
        heldLevels: Record<string, ScopeLevel>
        /** Whether the inviter owns the enterprise outright: only then is Full access offered. */
        heldIsOwner: boolean
        /**
         * Whether this picker draws the per-resource grid itself. A form that already has the plain
         * per-resource picker — the People screen's invite form, which had one before presets existed — leaves
         * that one in place until a preset starts a grant, and hands the grid over here once one has, so the
         * same twenty resources are never drawn twice for one grant.
         */
        showLevels?: boolean
    }>(),
    { showLevels: true },
)

const emit = defineEmits<{
    /** The whole per-resource grant as it now stands, whether a preset or a single resource changed it. */
    'update:levels': [levels: Record<string, ScopeLevel>]
    /**
     * Whether this grant is "owns the enterprise outright" — Full access, which enumerates nothing. Emitted
     * alongside the levels so the form sending the patch knows to send `isOwner` rather than a scope list.
     */
    'update:isOwner': [isOwner: boolean]
    /**
     * Which preset was just chosen, for a form that shows something different once a grant started from one
     * — nothing is recorded anywhere: a preset is a starting point at the moment of granting, not a role.
     */
    'update:preset': [preset: GrantPresetKey]
}>()

/**
 * What each preset produced, kept only so the screen can say the grant has since been adjusted away from it.
 * The levels as applied, not the preset's own table, so an inviter who holds less than a preset asks for is
 * not told their grant is customized the instant they choose one. Null until a preset is chosen — setting
 * every resource by hand is no preset at all, and so can never read as drift.
 */
const appliedPresetLevels = ref<Record<string, ScopeLevel> | null>(null)
/** Which preset was chosen, named in the indicator so "customized" says what it has drifted from. */
const chosenPreset = ref<GrantPreset | null>(null)

/** Only what this inviter may hand out: Full access is offered to an owner alone (#628). */
const offered = computed(() => GRANT_PRESETS.filter(preset => !preset.ownerOnly || props.heldIsOwner))

const customized = computed(() => isCustomizedFromPreset(props.levels, appliedPresetLevels.value))

/**
 * Applies one preset, capped at no more than the inviter holds. Full access enumerates nothing — owning is
 * the whole grant — so it clears every level and says `isOwner` instead; the enumerated presets say the
 * opposite, in case Full access was chosen first and then thought better of.
 */
function choose(preset: GrantPreset): void {
    chosenPreset.value = preset
    emit('update:preset', preset.key)
    if (!preset.levels) {
        appliedPresetLevels.value = null
        emit('update:levels', everyResourceAt('none'))
        emit('update:isOwner', true)
        return
    }
    const applied = applyPresetLevels(preset, props.heldLevels, props.heldIsOwner)
    appliedPresetLevels.value = { ...applied }
    emit('update:levels', applied)
    emit('update:isOwner', false)
}

/**
 * Forgets which preset produced the grant on screen, without touching the grant itself.
 *
 * This picker is mounted once and kept — the invite form it sits in does not remount between invitations —
 * so whoever owns the grant has to say when it starts over, as the People screen's invite form does once an
 * invitation has been sent. Without being told, `chosenPreset` and `appliedPresetLevels` would outlive the
 * grant they describe: the next, untouched invitation would come back with its levels reset to `none` while
 * this component still held the last preset's applied table, so the blank grant would read as drift and the
 * screen would show "<last preset> · customized" beside a still-highlighted preset button that nobody on
 * this invitation had chosen.
 */
function reset(): void {
    chosenPreset.value = null
    appliedPresetLevels.value = null
}

defineExpose({ reset })

/**
 * One resource changed by hand. The preset that was chosen is deliberately left as it was, so the change
 * reads as an adjustment of that preset rather than silently becoming a grant nobody can trace — and the
 * level itself is never re-applied from the preset, so the change sticks.
 */
function setLevel(resource: string, level: ScopeLevel): void {
    emit('update:levels', { ...props.levels, [resource]: level })
}
</script>

<template>
	<!--
		`grant-preset-picker` shares a prefix with each `grant-preset-<key>` button on purpose: it is the
		handle for the whole control, and a test looking for the offered presets by prefix reads the buttons.
	-->
	<div data-testid="grant-preset-picker" class="space-y-4">
		<div class="space-y-2">
			<p class="text-muted-foreground text-sm">
				Start from how this person will actually work, then adjust anything before you send it. Nothing
				here grants more than you hold yourself.
			</p>
			<div class="flex flex-wrap items-center gap-2">
				<!-- The chosen preset is the filled kit button; the rest stay outlined until one is chosen. -->
				<Button
					v-for="preset in offered"
					:key="preset.key"
					type="button"
					size="sm"
					:variant="chosenPreset?.key === preset.key ? 'default' : 'outline'"
					:aria-pressed="chosenPreset?.key === preset.key"
					:data-testid="`grant-preset-${preset.key}`"
					@click="choose(preset)"
				>{{ preset.label }}</Button>
				<!-- Said outright once the grant no longer matches the preset chosen: a preset is a starting point. -->
				<ZhSystemLabel v-if="customized" as="span" tone="system" data-testid="grant-customized">
					{{ chosenPreset ? `${chosenPreset.label} · customized` : 'Customized' }}
				</ZhSystemLabel>
			</div>
		</div>

		<!--
			The same per-resource picker as ever, preset or not: whoever wants to set all twenty by hand still
			can, and a preset only fills these in.
		-->
		<div v-if="showLevels" class="grid gap-2 sm:grid-cols-2">
			<div
				v-for="resource in API_TOKEN_RESOURCES"
				:key="resource"
				class="flex items-center justify-between gap-2 text-sm"
			>
				<span>{{ resourceLabel(resource) }}</span>
				<Select
					:model-value="levels[resource] ?? 'none'"
					:data-testid="`grant-level-${resource}`"
					@update:model-value="setLevel(resource, $event as ScopeLevel)"
				>
					<SelectTrigger size="sm" :aria-label="`What this person may do with ${resourceLabel(resource)}`">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="none">No access</SelectItem>
						<SelectItem value="read">Read</SelectItem>
						<SelectItem value="write">Read and write</SelectItem>
					</SelectContent>
				</Select>
			</div>
		</div>
	</div>
</template>
